import { Plus as AddRoundedIcon, MenuDown as ArrowDropDownRoundedIcon } from '@opengov/react-capital-assets';

import { ButtonGroup, Tooltip } from '@mui/material';
import React, { MutableRefObject } from 'react';

import { ActionButton } from '../../Components/ActionButton';
import { tooltipOffsetProps } from '../../Components/styleConstants';
import { DATA_OG, DATA_TEST, getDynamicDataOGId, getDynamicDataTestId } from '../../enums';
import { ICreateNewActionItem } from '../../types';

import { CreateNewActionSubmenu } from './CreateNewActionSubmenu';

interface ICreateNewActionButtonGroupProps {
  actionItems: ICreateNewActionItem[];
  condensed?: boolean;
  showCondensedLabel?: boolean;
}

export const CreateNewActionButtonGroup: React.FC<ICreateNewActionButtonGroupProps> = ({
  actionItems,
  condensed,
  showCondensedLabel
}) => {
  const [displayedAction, setDisplayedAction] = React.useState<ICreateNewActionItem>(actionItems[0]);
  const [showActionSelectSubmenu, setShowActionSelectSubmenu] = React.useState(false);
  const actionButtonRef = React.useRef<HTMLButtonElement>(null);
  const firstSubmenuItemRef = React.useRef<HTMLLIElement>(null);
  React.useEffect(() => {
    /*
     * If the displayed action is not in the list of available action items,
     * set the displayed action to the first item in the list
     */
    if (!actionItems.some((item) => item.label === displayedAction.label)) {
      setDisplayedAction(actionItems[0]);
    }
  }, [actionItems, displayedAction]);

  const isSplitButton = !condensed && actionItems.length > 1;

  return (
    <>
      <ButtonGroup
        data-test={DATA_TEST.create_new_action_button_group}
        variant="outlined"
        sx={{
          display: 'flex',
          flexShrink: 0
        }}
        aria-label={isSplitButton ? 'split-button' : undefined}
      >
        {!condensed && (
          <ActionButton
            dataTest={getDynamicDataTestId(DATA_TEST.create_new_action_button, displayedAction?.label)}
            dataOg={getDynamicDataOGId(DATA_OG.create_new_action_button, displayedAction?.dataOg || '')}
            accessibility={{}}
            IconPrefix={AddRoundedIcon}
            label={displayedAction?.label}
            onClick={displayedAction.handleCreate}
            slots={{
              button: {
                styleProps: {
                  pl: '4px !important',
                  height: '32px'
                }
              }
            }}
          />
        )}
        {(condensed || actionItems.length > 1) && (
          <Tooltip
            title={actionItems.length > 1 ? 'Select Item to Create' : `${displayedAction.label}`}
            placement="top"
            enterNextDelay={500}
            disableInteractive={true}
            {...tooltipOffsetProps}
          >
            <div style={{ display: 'inline-flex' }}>
              <ActionButton
                dataTest={DATA_TEST.create_new_action_dropdown_button}
                dataOg={DATA_OG.create_new_action_dropdown_button}
                IconPrefix={condensed ? AddRoundedIcon : ArrowDropDownRoundedIcon}
                IconSuffix={showCondensedLabel ? ArrowDropDownRoundedIcon : undefined}
                slots={{
                  icon: {
                    styleProps: {
                      fontSize: '1.5rem'
                    }
                  },
                  button: {
                    styleProps: {
                      height: 32,
                      ...(condensed && { marginY: 1, marginX: 0 }),
                      ...(showCondensedLabel && { pr: '1px !important', pl: '4px !important' }),
                      ...(!showCondensedLabel && condensed && { width: 32 }),
                      minWidth: '0 !important',
                      ...(!condensed && { width: 30 })
                    }
                  }
                }}
                label={showCondensedLabel ? 'Create' : undefined}
                accessibility={{
                  id: 'create_new_action_dropdown_button',
                  associatedMenuId: 'create_new_action_dropdown_menu',
                  expanded: showActionSelectSubmenu,
                  ariaLabel: 'actions menu'
                }}
                onClick={() => setShowActionSelectSubmenu((s) => !s)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' && firstSubmenuItemRef?.current) {
                    firstSubmenuItemRef.current.focus();
                  }
                }}
                ref={actionButtonRef}
              />
            </div>
          </Tooltip>
        )}
      </ButtonGroup>

      <CreateNewActionSubmenu
        anchorEl={actionButtonRef.current}
        firstSubmenuItemRef={firstSubmenuItemRef as MutableRefObject<HTMLLIElement>}
        open={showActionSelectSubmenu}
        actions={actionItems}
        accessibility={{
          associatedButtonId: 'create_new_action_dropdown_button',
          id: 'create_new_action_dropdown_menu'
        }}
        handleClose={() => setShowActionSelectSubmenu(false)}
        handleClick={(actionItem: ICreateNewActionItem) => {
          actionItem.handleCreate();
          setDisplayedAction(actionItem);
          setShowActionSelectSubmenu(false);
        }}
      />
    </>
  );
};
